import {
  collection,
  CollectionReference,
  doc,
  getDoc,
  setDoc,
  UpdateData,
  updateDoc,
  where as firebaseWhere,
  WhereFilterOp,
  query,
  Query,
  or,
  orderBy as firebaseOrderby,
  limit as firebaseLimit,
  OrderByDirection,
  getCountFromServer,
  getAggregateFromServer,
  sum as firebaseSum,
  average as firebaseAverage,
  getDocs,
  startAt as firebaseStartAt,
  endAt as firebaseEndAt,
  startAfter as firebaseStartAfter,
  endBefore as firebaseEndBefore,
  DocumentData,
} from "firebase/firestore";
import { firestore } from "./firebase.config";
import { Profile } from "@/models/Profile.model";
import { CollectionNames, SubCollectionNames } from "./constants";
import { Job } from "@/models/Job.model";
import { Brand } from "@/models/Brands.model";
import { Event } from "@/models/Events.model";
import { ProfileJobJunction } from "@/models/ProfileJobJunction";

type CollectionNamesType = keyof typeof CollectionNames;
type SubCollectionNameType = keyof typeof SubCollectionNames;
function handleError(errorMessage: string) {
  switch (errorMessage) {
    case "Missing or insufficient permissions.":
      return "Firebase Permission error!";

    default:
      return errorMessage;
  }
}
export class FirebaseCollection<T extends DocumentData> {
  private collectionName: string;
  private collectionReference: CollectionReference<T, T>;
  private query: Query<T, T> | null = null;
  constructor(collectionName: CollectionNamesType) {
    this.collectionReference = collection(
      firestore,
      CollectionNames[collectionName]
    ) as CollectionReference<T, T>;

    this.collectionName = CollectionNames[collectionName];
    return this;
  }

  /**
   *
   * @param documentId Document id of parent document
   * @param subCollection Sub collection name
   */
  public _subCollection(
    documentId: string,
    subCollection: SubCollectionNameType
  ) {
    console.log({
      collecitonName: this.collectionName,
      documentId,
      subCollectionName: SubCollectionNames[subCollection],
    });

    const documentRef = doc(firestore, this.collectionName, documentId);
    // the collection is a part of sub-collection -> update collection reference
    this.collectionReference = collection(
      documentRef,
      SubCollectionNames[subCollection]
    ) as CollectionReference<T, T>;

    return this;
  }

  /**
   *
   * @param documentId id of the document
   * @returns
   */
  doc(documentId?: string) {
    console.log({ documentId, collectionName: this.collectionName });

    let documentRef = doc(this.collectionReference);

    if (documentId) {
      documentRef = doc(this.collectionReference, documentId);
    }

    async function get() {
      try {
        return await getDoc(documentRef);
      } catch (error) {
        throw handleError((error as Error).message);
      }
    }

    function set(payload: T) {
      return setDoc(documentRef, payload);
    }

    function update(payload: Partial<T>) {
      // Typecase to avoid partial warning!
      const _payload = payload as UpdateData<T>;
      return updateDoc(documentRef, _payload);
    }

    function ref() {
      return documentRef;
    }

    const id = documentRef.id;

    return { get, set, update, ref, id };
  }

  /**
   *
   */
  where(
    fieldKey: keyof T,
    filter: WhereFilterOp,
    value: string | number | boolean | string[]
  ) {
    this.query = query(
      this.query || this.collectionReference,
      firebaseWhere(fieldKey as string, filter, value)
    );
    return this;
  }

  /**
   * Gets all the documents with OR logic.
   *
   * i.e. Get all the documents where category is "TV" or "Mice"
   *
   * whereOR(
   *
   *        {"category", "==", "TV"},
   *
   *        {"category", "==", "Mice"}
   *
   * );
   *
   * @param where
   * @param OR
   * @returns
   */
  whereOR(
    where: {
      fieldKey: keyof T;
      filter: WhereFilterOp;
      value: string | number | boolean;
    },
    OR: {
      fieldKey: keyof T;
      filter: WhereFilterOp;
      value: string | number | boolean;
    }
  ) {
    this.query = query(
      this.query || this.collectionReference,
      or(
        firebaseWhere(where.fieldKey as string, where.filter, where.value),
        firebaseWhere(OR.fieldKey as string, OR.filter, OR.value)
      )
    );
    return this;
  }

  orderBy(fieldKey: keyof T, directionStr: OrderByDirection = "asc") {
    this.query = query(
      this.query || this.collectionReference,
      firebaseOrderby(fieldKey as string, directionStr)
    );
    return this;
  }

  limit(limit: number) {
    this.query = query(
      this.query || this.collectionReference,
      firebaseLimit(limit)
    );

    return this;
  }

  count() {
    return getCountFromServer(this.query || this.collectionReference);
  }

  sum(fieldKey: keyof T) {
    return getAggregateFromServer(this.query || this.collectionReference, {
      sum: firebaseSum(fieldKey as string),
    });
  }

  average(fieldKey: keyof T) {
    return getAggregateFromServer(this.query || this.collectionReference, {
      average: firebaseAverage(fieldKey as string),
    });
  }

  startAt(value: number | string) {
    this.query = query(
      this.query || this.collectionReference,
      firebaseStartAt(value)
    );

    return this;
  }
  endAt(value: number | string) {
    this.query = query(
      this.query || this.collectionReference,
      firebaseEndAt(value)
    );

    return this;
  }
  startAfter(value: number | string) {
    this.query = query(
      this.query || this.collectionReference,
      firebaseStartAfter(value)
    );

    return this;
  }
  endBefore(value: number | string) {
    this.query = query(
      this.query || this.collectionReference,
      firebaseEndBefore(value)
    );

    return this;
  }

  /**Query reference */
  ref() {
    return this.query || query(this.collectionReference);
  }
  get() {
    return getDocs(this.ref());
  }
}
export const collectionRef = {
  Profiles: new FirebaseCollection<Profile>("Profiles"),
  Jobs: new FirebaseCollection<Job>("Jobs"),
  Brands: new FirebaseCollection<Brand>("Brands"),
  Events: new FirebaseCollection<Event>("Events"),
  ProfileJobJunction: new FirebaseCollection<ProfileJobJunction>(
    "ProfileJobJunction"
  ),
} as const;
